/**
 * ConfirmDeleteUserDialog Component
 *
 * A destructive WARNING modal for permanently deleting a member's account.
 *
 * Deleting a team member performs a real, irreversible ZITADEL account deletion
 * (`RemoveUser`) — team members are resource-owned by this org, so the account
 * ceases to exist. Because this can't be undone, the modal:
 *  - states the consequence plainly (permanent account deletion, cannot be
 *    undone), names the user, and uses destructive ("error") styling;
 *  - requires a deliberate type-to-confirm gesture — the admin must type the
 *    user's email before the red confirm button enables — so it can't be
 *    triggered by a reflexive click on a default-focused OK.
 *
 * The dialog is "dumb" about which org it acts on: the store's `deleteUser`
 * action resolves the effective org id (active org for a normal admin, picked
 * org for a staff admin). This component only needs the target {@link User} and
 * the async confirm handler.
 *
 * @module components/ConfirmDeleteUserDialog
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Box,
  TextField,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { User } from '../types';
import { isDeleteUserError } from '../stores/useUsersStore';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the ConfirmDeleteUserDialog component.
 */
export interface ConfirmDeleteUserDialogProps {
  /**
   * The user to delete. `null` keeps the dialog closed; a non-null value opens
   * it. Passing the user (rather than a separate `open` flag) keeps the target
   * and the open state in lockstep so they can never disagree.
   */
  user: User | null;

  /** Callback when the dialog should close (cancel or after a successful delete). */
  onClose: () => void;

  /**
   * Async handler that performs the deletion. Resolves on success; rejects with
   * a {@link DeleteUserError} (or any Error) on failure. The dialog shows a
   * spinner while it's pending and an inline alert if it rejects.
   */
  onConfirm: (userId: string) => Promise<void>;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Best-effort human label for the user being deleted: display name, else the
 * given/family name, else the email. Mirrors the data grid's Name column.
 */
function userLabel(user: User): string {
  if (user.displayName) {
    return user.displayName;
  }
  const fullName = [user.givenName, user.familyName].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }
  return user.email;
}

// =============================================================================
// ConfirmDeleteUserDialog Component
// =============================================================================

/**
 * ConfirmDeleteUserDialog - Destructive warning modal for deleting an account.
 *
 * Features:
 * - Plain-language warning: permanent account deletion, cannot be undone
 * - Names the target user (display name / full name / email) and their email
 * - Type-to-confirm: the admin must type the user's email to enable the button
 * - Destructive ("error" color) confirm button, never default-focused
 * - Loading state while the delete is in flight (inputs + buttons disabled)
 * - Inline error alert on failure; the dialog stays open so the admin can retry
 * - Resets its transient state each time it (re)opens
 */
export function ConfirmDeleteUserDialog({
  user,
  onClose,
  onConfirm,
}: ConfirmDeleteUserDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const open = user !== null;

  // Reset transient state on each open transition. Mirrors the pattern used by
  // InviteUserDialog (adjust-state-during-render, not useEffect).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setError(null);
      setIsSubmitting(false);
      setConfirmText('');
    }
  }

  // The type-to-confirm gate: the admin must type the user's email exactly
  // (case-insensitive, trimmed) before the destructive button enables.
  const confirmMatches =
    user !== null &&
    confirmText.trim().toLowerCase() === user.email.trim().toLowerCase();
  const canConfirm = confirmMatches && !isSubmitting;

  const handleCancel = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [isSubmitting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!user || !confirmMatches || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(user.id);
      // Success — parent closes the dialog and shows a snackbar.
      onClose();
    } catch (err) {
      const message = isDeleteUserError(err)
        ? err.message
        : 'Something went wrong deleting the user. Please try again.';
      setError(message);
      setIsSubmitting(false);
    }
  }, [user, confirmMatches, isSubmitting, onConfirm, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      data-testid="delete-user-dialog"
      aria-labelledby="delete-user-dialog-title"
    >
      <DialogTitle
        id="delete-user-dialog-title"
        data-testid="delete-user-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
        }}
      >
        <WarningAmberIcon color="error" />
        Delete user permanently
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="delete-user-error"
          >
            {error}
          </Alert>
        )}

        {user && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" data-testid="delete-user-warning">
              This <strong>permanently deletes</strong>{' '}
              <strong>{userLabel(user)}</strong> ({user.email}) and{' '}
              <strong>cannot be undone</strong>. The user's account is removed
              and they lose access immediately.
            </Alert>

            <DialogContentText data-testid="delete-user-dialog-text">
              To confirm, type the user's email <strong>{user.email}</strong>{' '}
              below.
            </DialogContentText>

            <TextField
              label="Type the email to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isSubmitting}
              autoFocus
              fullWidth
              autoComplete="off"
              data-testid="delete-user-confirm-field"
              slotProps={{
                htmlInput: {
                  'data-testid': 'delete-user-confirm-input',
                  'aria-label': 'Type the email to confirm',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          data-testid="delete-user-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!canConfirm}
          data-testid="delete-user-confirm-button"
        >
          {isSubmitting ? 'Deleting...' : 'Delete user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
